/**
 * @file PhoneOrderField.tsx
 * @description Порядок колонок на телефоне для 2–4 колонок (phase19.16,
 * партия SB120, этап 1; uiux-sb120-stage1 §2.6; данные
 * db-design-sb120-stage1 §1 `columns.phoneOrder` — перестановка 1..N, нет
 * ключа — по порядку):
 *
 * ```
 * На телефоне по порядку
 * ≡ 1  Колонка 2 · Фото                 [▲][▼]   ← 56 px
 * ≡ 2  Колонка 1 · Заголовок «Иван-чай…» [▲][▼]
 *                                        [↺ как на ПК]
 * ```
 *
 * Перетаскивание (@dnd-kit, ручка ≡) или ▲▼; порядок «как на ПК» не
 * хранится. Подпись колонки — по первому элементу, до 28 знаков.
 */

import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import type { SectionColumns } from "@chaiberi/shared";
import { RotateCcw } from "lucide-react";

import { Button } from "../../../ui/button";
import { Label } from "../../../ui/label";
import { PhoneOrderRow } from "./PhoneOrderRow";
import { DND_ACCESSIBILITY_RU } from "../../../../lib/site-builder/dnd-ru";
import { columnCaption } from "../../../../lib/site-builder/section-meta";

interface Props {
  columns: SectionColumns;
  onChange: (order: number[] | undefined) => void;
}

/** Порядок по ПК — не хранится (нет ключа). */
const isIdentity = (order: readonly number[]) => order.every((v, i) => v === i + 1);

export function PhoneOrderField({ columns, onChange }: Props) {
  const count = columns.items.length;
  const order = columns.phoneOrder ?? Array.from({ length: count }, (_, i) => i + 1);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const write = (next: number[]) => onChange(isIdentity(next) ? undefined : next);
  const move = (from: number, to: number) => {
    if (to < 0 || to >= order.length) return;
    write(arrayMove([...order], from, to));
  };
  function onDragEnd(event: DragEndEvent) {
    const from = order.indexOf(Number(event.active.id));
    const to = event.over ? order.indexOf(Number(event.over.id)) : -1;
    if (from >= 0 && to >= 0 && from !== to) move(from, to);
  }

  if (count < 2) return null;
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex min-h-8 items-center justify-between gap-2">
        <Label>На телефоне по порядку</Label>
        {columns.phoneOrder ? (
          <Button variant="ghost" size="sm" className="h-12 can-hover:h-8" onClick={() => onChange(undefined)} aria-label="Порядок на телефоне: как на ПК">
            <RotateCcw aria-hidden />
            как на ПК
          </Button>
        ) : (
          <span className="type-caption text-on-surface-variant">как на ПК</span>
        )}
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd} accessibility={DND_ACCESSIBILITY_RU}>
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <ol className="flex flex-col gap-1">
            {order.map((col, i) => (
              <PhoneOrderRow
                key={col}
                column={col}
                place={i + 1}
                caption={columnCaption(columns.items[col - 1] ?? { elements: [] })}
                first={i === 0}
                last={i === order.length - 1}
                onUp={() => move(i, i - 1)}
                onDown={() => move(i, i + 1)}
              />
            ))}
          </ol>
        </SortableContext>
      </DndContext>
    </div>
  );
}
